"use client";

import { useState, useEffect, useRef } from "react";
import MainLayout from "../sidebar/Sidebar.js";
import api from "../api/api.js";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isToday,
  addMonths,
  subMonths,
  isWithinInterval,
  addDays,
} from "date-fns";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

export default function SchedulingPage() {
  const [location, setLocation] = useState("Scheduling");
  const [events, setEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [modalEvent, setModalEvent] = useState(null);
  const [hoveredElementRect, setHoveredElementRect] = useState(null); // New state to store the hovered element's rect

  const calendarRef = useRef(null);
  const modalRef = useRef(null);

  const isMobile =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  useEffect(() => {
    async function fetchTodos() {
      try {
        const response = await api.get("/todos/");
        const todos = response.data || [];
        const mappedEvents = todos
          .filter((todo) => todo.deadline)
          .map((todo) => {
            const [year, month, day] = todo.deadline.split("-").map(Number);
            return {
              id: todo.id,
              title: todo.task_name,
              description: todo.description,
              assign_to: todo.assign_to,
              urgency: todo.urgency,
              project_id: todo.project_id,
              status: todo.status,
              deadline: todo.deadline,
              date: new Date(year, month - 1, day),
            };
          });
        setEvents(mappedEvents);
      } catch (e) {
        console.error("Failed to load todos for calendar:", e);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    }
    setLocation("Scheduling");
    fetchTodos();
  }, []);

  // Effect to calculate modal position after it renders and dimensions are available
  useEffect(() => {
    if (
      modalEvent &&
      hoveredElementRect &&
      calendarRef.current &&
      modalRef.current
    ) {
      const calendarRect = calendarRef.current.getBoundingClientRect();
      const modalHeight = modalRef.current.offsetHeight;
      const modalWidth = modalRef.current.offsetWidth;

      // Calculate top position: hovered element's top relative to calendar, then move up by modal height + padding
      let newTop = hoveredElementRect.top - calendarRect.top - modalHeight - 10; // 10px padding above

      // Calculate left position: hovered element's left relative to calendar
      let newLeft = hoveredElementRect.left - calendarRect.left;

      // Adjust if modal goes off-screen to the left
      if (newLeft < 0) {
        newLeft = 0; // Align to the left edge of the calendar
      }

      // Adjust if modal goes off-screen to the right
      if (newLeft + modalWidth > calendarRect.width) {
        newLeft = calendarRect.width - modalWidth; // Align to the right edge of the calendar
      }

      // Adjust if modal goes off-screen to the top (place it below the event instead)
      if (newTop < 0) {
        newTop = hoveredElementRect.bottom - calendarRect.top + 10; // 10px padding below
      }

      setModalPosition({ top: newTop, left: newLeft });
    }
  }, [modalEvent, hoveredElementRect, calendarRef, modalRef]); // Dependencies for recalculation

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  const getEventsForDay = (day) => events.filter((e) => isSameDay(e.date, day));

  const goToPreviousMonth = () => setCurrentMonth((prev) => subMonths(prev, 1));
  const goToNextMonth = () => setCurrentMonth((prev) => addMonths(prev, 1));

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const upcomingEvents = events
    .filter((e) =>
      isWithinInterval(e.date, {
        start: new Date(),
        end: addDays(new Date(), 15),
      })
    )
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const handleDayClick = (event) => {
    if (isMobile) {
      if (modalEvent && modalEvent.id === event.id) {
        setModalEvent(null);
        setHoveredElementRect(null); // Clear rect on mobile click
      } else {
        setModalEvent(event);
        setHoveredElementRect(null); // Clear rect for mobile click, as positioning might be different
      }
    }
  };

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="min-h-full py-4 px-6 relative">
        {/* Month Navigation */}
        <section className="flex items-center justify-between mb-6">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded hover:bg-gray-200"
            aria-label="Previous Month"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-semibold">
            {format(currentMonth, "MMMM yyyy")}
          </h2>
          <button
            onClick={goToNextMonth}
            className="p-2 rounded hover:bg-gray-200"
            aria-label="Next Month"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </section>

        {/* Calendar Grid */}
        <section className="relative" ref={calendarRef}>
          <div className="grid grid-cols-7 gap-1 text-center font-semibold text-gray-600 mb-1">
            {weekDays.map((day) => (
              <div key={day} className="py-1">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: startOfMonth(currentMonth).getDay() }).map(
              (_, i) => (
                <div key={`empty-${i}`} className="h-20"></div>
              )
            )}
            {daysInMonth.map((day) => {
              const dayEvents = getEventsForDay(day);
              const today = isToday(day);
              return (
                <div
                  key={day.toISOString()}
                  className={`rounded-md p-2 h-20 flex flex-col overflow-hidden border transition ${
                    today
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 bg-white"
                  } cursor-pointer`}
                  onClick={() => {
                    if (isMobile && dayEvents.length > 0) {
                      handleDayClick(dayEvents[0]);
                    }
                  }}
                >
                  <div className="text-sm font-semibold w-6 h-6 flex items-center justify-center rounded-full mb-1 text-gray-800">
                    {format(day, "d")}
                  </div>
                  <div className="flex flex-col gap-0.5 overflow-y-auto text-xs flex-grow">
                    {dayEvents.map((event) => (
                      <span
                        key={event.id}
                        className="truncate rounded px-1 py-0.5 bg-blue-600 text-white"
                        onMouseEnter={(e) => {
                          if (!isMobile) {
                            setHoveredElementRect(
                              e.currentTarget.getBoundingClientRect()
                            );
                            setModalEvent(event);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isMobile) {
                            setModalEvent(null);
                            setHoveredElementRect(null);
                          }
                        }}
                      >
                        {event.title}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Modal */}
          {modalEvent && (
            <div
              ref={modalRef}
              onMouseLeave={() => {
                // Only close if mouse leaves the modal and it's not mobile
                if (!isMobile) {
                  setModalEvent(null);
                  setHoveredElementRect(null);
                }
              }}
              className="z-50 max-w-md w-full bg-white border border-gray-300 rounded shadow-lg p-4 absolute"
              style={{
                top: modalPosition.top,
                left: modalPosition.left,
                transform: "none",
                pointerEvents: "auto",
                whiteSpace: "normal",
                width: "max-content",
                minWidth: "200px",
                maxWidth: "300px",
              }}
            >
              <button
                onClick={() => {
                  setModalEvent(null);
                  setHoveredElementRect(null);
                }}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
              <h3 className="text-lg font-semibold mb-2">{modalEvent.title}</h3>
              <p>
                <strong>Description:</strong> {modalEvent.description || "N/A"}
              </p>
              <p>
                <strong>Assigned To:</strong> {modalEvent.assign_to || "N/A"}
              </p>
              <p>
                <strong>Urgency:</strong> {modalEvent.urgency || "Medium"}
              </p>
              <p>
                <strong>Project ID:</strong> {modalEvent.project_id || "N/A"}
              </p>
              <p>
                <strong>Status:</strong> {modalEvent.status || "Assigned"}
              </p>
              <p>
                <strong>Deadline:</strong> {modalEvent.deadline || "N/A"}
              </p>
            </div>
          )}
        </section>

        {/* Upcoming Events */}
        <section className="mt-10">
          <h3 className="text-xl font-semibold mb-4">Upcoming Events</h3>
          {loading ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : upcomingEvents.length === 0 ? (
            <p className="text-center text-gray-500">No upcoming events.</p>
          ) : (
            <ul className="space-y-3 max-w-xl">
              {upcomingEvents.map((event) => (
                <li key={event.id}>
                  <div className="flex flex-col p-4 rounded-lg bg-gray-100 hover:bg-gray-200 transition">
                    <strong className="text-lg text-gray-900">
                      {event.title}
                    </strong>
                    <small className="text-gray-600">
                      {format(event.date, "PPP")}
                    </small>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
